import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import LoadingState from "@/components/feature/LoadingState";
import { useCrew } from "@/hooks/admin/useAdminData";
import { crewMembers as fallbackCrew } from "@/mocks/admin/crew";
import AvailabilityCalendar from "./components/AvailabilityCalendar";
import PerformancePanel from "./components/PerformancePanel";
import RecentShifts from "./components/RecentShifts";
import MoverLinkCard from "./components/MoverLinkCard";
import EditCrewModal from "./components/EditCrewModal";
import StatusRibbon from "./components/StatusRibbon";
import IdentityCard from "./components/IdentityCard";
import WorkTabs, { type TabKey } from "./components/WorkTabs";
import OverviewPanel from "./components/OverviewPanel";
import DocumentsPanel from "./components/DocumentsPanel";

export default function CrewDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: crewMembers, loading, refetch } = useCrew();
  const [editOpen, setEditOpen] = useState(false);
  const [toast, setToast] = useState("");
  const [tab, setTab] = useState<TabKey>("overview");

  const member =
    crewMembers.find((m) => m.id === id) ||
    crewMembers[0] ||
    fallbackCrew.find((m) => m.id === id) ||
    fallbackCrew[0];

  if (loading && crewMembers.length === 0) {
    return (
      <AdminLayout>
        <div className="px-4 md:px-8 py-6 md:py-8">
          <LoadingState message="Loading crew member…" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-[1600px] mx-auto">
        {/* Breadcrumb + back */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 text-xs text-ink-500 min-w-0">
            <Link to="/dashboard" className="hover:text-ink-900 cursor-pointer whitespace-nowrap">
              Dashboard
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/crew" className="hover:text-ink-900 cursor-pointer whitespace-nowrap">
              Crew
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-ink-900 font-semibold truncate">{member.name}</span>
          </div>
          <Link
            to="/crew"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to crew
          </Link>
        </div>

        {/* Live status ribbon */}
        <StatusRibbon member={member} />

        {/* Main 2-column dossier layout */}
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-4 md:gap-5">
          {/* Left sticky identity */}
          <div className="lg:sticky lg:top-6 lg:self-start space-y-4 md:space-y-5">
            <IdentityCard member={member} onEdit={() => setEditOpen(true)} />
            <MoverLinkCard member={member} />
          </div>

          {/* Right work area with tabs */}
          <div className="space-y-4 md:space-y-5 min-w-0">
            <WorkTabs active={tab} onChange={setTab} />

            {tab === "overview" && <OverviewPanel member={member} />}
            {tab === "schedule" && <AvailabilityCalendar member={member} />}
            {tab === "performance" && <PerformancePanel member={member} />}
            {tab === "shifts" && <RecentShifts crewId={member.id} />}
            {tab === "files" && <DocumentsPanel />}
          </div>
        </div>
      </div>

      <EditCrewModal
        open={editOpen}
        member={member}
        onClose={() => setEditOpen(false)}
        onSaved={async () => {
          await refetch();
          setToast("Profile updated");
          setTimeout(() => setToast(""), 2400);
        }}
      />

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-ink-900 text-white text-sm font-semibold px-4 py-3 rounded-md flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-emerald-400"></i>
          {toast}
        </div>
      )}
    </AdminLayout>
  );
}